import type React from 'react';
import { useEffect } from 'react';
import seoLaunchPolicy from '../../config/seoLaunchPolicy.json';

interface SeoProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  noindex?: boolean;
}

const INDEXABLE_CORE_PATHS = new Set(seoLaunchPolicy.corePaths);
const INDEXABLE_LOCALES = new Set(seoLaunchPolicy.indexableLocales.map((locale) => locale.split('-')[0]));
const INDEXABLE_DETAIL_IDS = Object.values(seoLaunchPolicy.details).map((policy) => ({
  prefix: policy.prefix,
  ids: new Set(policy.ids),
}));

const isIndexableLaunchPath = (pathname: string) => {
  const locale = pathname.split('/')[1];
  if (!INDEXABLE_LOCALES.has(locale)) return false;

  const basePath = pathname.slice(locale.length + 1) || '/';
  if (INDEXABLE_CORE_PATHS.has(basePath)) return true;

  return INDEXABLE_DETAIL_IDS.some(({ prefix, ids }) => (
    basePath.startsWith(`${prefix}/`) && ids.has(decodeURIComponent(basePath.slice(prefix.length + 1)))
  ));
};

const upsertMeta = (selector: string, create: () => HTMLMetaElement, value: string, attr: 'content' = 'content') => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  element.setAttribute(attr, value);
};

const upsertCanonical = (href: string) => {
  let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = href;
};

const Seo: React.FC<SeoProps> = ({ title, description, path, image = '/catalog/photos/home-coastal-fishing.jpg', noindex }) => {
  useEffect(() => {
    const siteUrl = (import.meta.env.VITE_SITE_URL || window.location.origin).replace(/\/+$/, '');
    const localePrefix = window.location.pathname.match(/^\/(en|it|es|fr|de)(?=\/|$)/)?.[0] || '';
    const localizedPath = localePrefix && !path.startsWith(`${localePrefix}/`) && path !== localePrefix
      ? `${localePrefix}${path === '/' ? '' : path}`
      : path;
    const canonical = `${siteUrl}${localizedPath === '/' ? '/' : localizedPath}`;
    const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
    const effectiveNoindex = noindex ?? !isIndexableLaunchPath(localizedPath);

    document.title = title;
    upsertMeta('meta[name="description"]', () => {
      const element = document.createElement('meta');
      element.name = 'description';
      return element;
    }, description);
    upsertMeta('meta[name="robots"]', () => {
      const element = document.createElement('meta');
      element.name = 'robots';
      return element;
    }, effectiveNoindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    upsertCanonical(canonical);
    upsertMeta('meta[property="og:title"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:title');
      return element;
    }, title);
    upsertMeta('meta[property="og:description"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:description');
      return element;
    }, description);
    upsertMeta('meta[property="og:url"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:url');
      return element;
    }, canonical);
    upsertMeta('meta[property="og:image"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('property', 'og:image');
      return element;
    }, imageUrl);
  }, [description, image, noindex, path, title]);

  return null;
};

export default Seo;
